import React from 'react'
import { Alert, Box, Typography } from '@mui/material'
import type { MergePreview } from './_shared'

export interface MergeItem {
  resource_type?: string
  payload: Record<string, unknown>
  target_name?: string | null
  target_uuid?: string | null
  target_survivor_uuid?: string | null
  target_survivor_name?: string | null
}

function describe(name?: string | null, uuid?: string | null): string {
  return name ?? uuid ?? 'unknown record'
}

/**
 * Produce a one-line summary for a merge proposed edit.
 *
 * Format: Delete Community Grant (dup) and fold it into Community Grant
 *
 * Two uuids tell a reviewer nothing, and approving is irreversible, so the
 * summary names both records and says plainly which one goes.
 */
export function mergeSummary(item: MergeItem): string {
  const loser = describe(item.target_name, item.target_uuid)
  const survivor = describe(
    item.target_survivor_name,
    item.target_survivor_uuid ?? (item.payload?.survivor_uuid as string),
  )
  return `Delete ${loser} and fold it into ${survivor}`
}

/**
 * Field values the merge sets on the surviving record. Anything not listed
 * keeps the survivor's existing value.
 */
export function mergeFieldResolutions(item: MergeItem): [string, unknown][] {
  const resolutions = item.payload?.field_resolutions
  if (!resolutions || typeof resolutions !== 'object') return []
  return Object.entries(resolutions as Record<string, unknown>)
}

/**
 * How each of a merged funder's opportunities is dealt with: carried across
 * to the surviving funder, or merged into one of its own. Opportunities the
 * proposal does not name are carried across.
 */
export function mergeOppDispositions(item: MergeItem): [string, string][] {
  const dispositions = item.payload?.opp_dispositions
  if (!dispositions || typeof dispositions !== 'object') return []
  return Object.entries(dispositions as Record<string, unknown>).map(
    ([oppUuid, disposition]) => {
      if (disposition && typeof disposition === 'object') {
        const target = (disposition as Record<string, unknown>).merge_into
        return [oppUuid, `merged into ${String(target)}`]
      }
      return [oppUuid, 'moved to the surviving funder']
    },
  )
}

function displayValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '(empty)'
  if (Array.isArray(value)) return value.length ? value.join(', ') : '(empty)'
  return String(value)
}

/**
 * Where an incoming value came from, so a reviewer can tell a value carried
 * across from the record being deleted from one the submitter typed.
 */
function valueSource(
  preview: MergePreview | null,
  field: string,
  incoming: unknown,
): string {
  const conflict = preview?.field_conflicts?.find((c) => c.field === field)
  if (!conflict) return ''
  if (String(conflict.loser_value) === String(incoming)) {
    return ' — from the record being deleted'
  }
  if (String(conflict.survivor_value) === String(incoming)) {
    return ' — unchanged'
  }
  return ' — entered by the submitter'
}

interface MergeSummaryProps {
  item: MergeItem
  preview?: MergePreview | null
  /**
   * One per opportunity given a merge_into disposition. A funder merge runs
   * an opportunity merge for each, and those carry their own blockers.
   */
  dispositionPreviews?: { oppUuid: string; preview: MergePreview }[]
  previewLoading?: boolean
  /**
   * The impact could not be read. Distinct from having no preview at all,
   * which is the ordinary state of a merge that has already been approved.
   */
  previewFailed?: boolean
  /**
   * Rendered in a list beside other edits, where the impact is never
   * fetched — reading both records per row would be one heavy request each.
   */
  summaryOnly?: boolean
}

/**
 * Renders a merge proposed edit for review.
 *
 * There is no diff table: a merge is not a field edit, and its effects are not
 * expressible as before/after cells. What a reviewer needs is which record
 * disappears, what the surviving record inherits, and — since approving cannot
 * be undone — what is discarded along the way.
 *
 * The detail comes from a live read of both records, so it is only available
 * while the merge is pending. Once approved the record it describes is gone,
 * and the summary falls back to what the proposal itself recorded.
 */
export const MergeSummary: React.FC<MergeSummaryProps> = ({
  item,
  preview = null,
  dispositionPreviews = [],
  previewLoading = false,
  previewFailed = false,
  summaryOnly = false,
}) => {
  const resolutions = mergeFieldResolutions(item)
  const dispositions = new Map(mergeOppDispositions(item))
  const survivorOppNames = new Map(
    (preview?.survivor_opportunities ?? []).map((o) => [
      o.uuid,
      o.fund_name ?? o.uuid,
    ]),
  )
  const survivorName =
    item.target_survivor_name ?? preview?.survivor.name ?? 'the surviving record'

  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 1 }}>
        {mergeSummary(item)}
      </Typography>
      <Alert severity="warning" sx={{ mb: 2, borderRadius: 1 }}>
        Approving deletes the first record. Its applications, matches,
        locations and funding rounds move to the surviving record first.
      </Alert>

      {summaryOnly && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 1 }}>
          Open this edit to see what it moves, what it discards, and whether
          anything blocks it.
        </Alert>
      )}
      {previewLoading && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 1 }}>
          Reading what this merge would do…
        </Alert>
      )}
      {previewFailed && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 1 }}>
          What this merge would do could not be read, so nothing below is a
          full account of it. Approving is disabled until it loads — reopen
          this edit to try again.
        </Alert>
      )}

      {preview?.loser_opportunities && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Opportunities under the record being deleted
          </Typography>
          {preview.loser_opportunities.length === 0 && (
            <Typography variant="body2">None.</Typography>
          )}
          {preview.loser_opportunities.map((opp) => {
            const disposition = dispositions.get(opp.uuid)
            const mergedInto = disposition?.startsWith('merged into ')
              ? disposition.slice('merged into '.length)
              : null
            return (
              <Typography variant="body2" key={opp.uuid} sx={{ mb: 0.5 }}>
                <strong>{opp.fund_name ?? opp.uuid}</strong>{' '}
                {mergedInto
                  ? `→ merged into ${survivorOppNames.get(mergedInto) ?? mergedInto}`
                  : `→ moves to ${survivorName}`}
              </Typography>
            )
          })}
        </Box>
      )}

      {resolutions.length > 0 && (
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            borderRadius: 1,
            bgcolor: 'grey.100',
            border: 1,
            borderColor: 'grey.300',
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Changes to {survivorName}
          </Typography>
          {resolutions.map(([field, value]) => {
            const conflict = preview?.field_conflicts?.find(
              (c) => c.field === field,
            )
            return (
              <Typography variant="body2" key={field} sx={{ mb: 0.5 }}>
                <strong>{field}:</strong>{' '}
                {conflict ? (
                  <>
                    <Box
                      component="span"
                      sx={{ color: 'text.secondary', textDecoration: 'line-through' }}
                    >
                      {displayValue(conflict.survivor_value)}
                    </Box>{' '}
                    → <strong>{displayValue(value)}</strong>
                  </>
                ) : (
                  <strong>{displayValue(value)}</strong>
                )}
                <Typography
                  component="span"
                  variant="caption"
                  color="text.secondary"
                >
                  {valueSource(preview, field, value)}
                </Typography>
              </Typography>
            )
          })}
        </Box>
      )}

      {preview && <MergeApplicationCollisions preview={preview} />}
      {preview && <MergeDiscards item={item} preview={preview} />}

      {preview?.warnings?.map((warning) => (
        <Alert key={warning} severity="warning" sx={{ mb: 1, borderRadius: 1 }}>
          {warning}
        </Alert>
      ))}
      {preview?.blockers?.map((blocker) => (
        <Alert key={blocker} severity="error" sx={{ mb: 1, borderRadius: 1 }}>
          {blocker}
        </Alert>
      ))}
      {dispositionPreviews.flatMap(({ oppUuid, preview: oppPreview }) =>
        oppPreview.blockers.map((blocker) => (
          <Alert
            key={`${oppUuid}-${blocker}`}
            severity="error"
            sx={{ mb: 1, borderRadius: 1 }}
          >
            {oppPreview.loser.name ?? oppUuid}: {blocker}
          </Alert>
        )),
      )}
      {dispositionPreviews.flatMap(({ oppUuid, preview: oppPreview }) =>
        oppPreview.warnings.map((warning) => (
          <Alert
            key={`${oppUuid}-${warning}`}
            severity="warning"
            sx={{ mb: 1, borderRadius: 1 }}
          >
            {oppPreview.loser.name ?? oppUuid}: {warning}
          </Alert>
        )),
      )}
    </Box>
  )
}

/**
 * Organisations holding an application against both records for one need.
 *
 * Only one can be visible, so the merge keeps the application recording more
 * — money received, then a funder's decision, then having applied — and
 * retains the other behind it. Nothing is deleted, but which one a customer
 * sees changes, so a reviewer is shown every pair.
 */
const MergeApplicationCollisions: React.FC<{ preview: MergePreview }> = ({
  preview,
}) => {
  const collisions = preview.application_collisions ?? []
  if (collisions.length === 0) return null

  const label = (status: string | null) => status ?? 'draft'

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Applications held against both records
      </Typography>
      {collisions.map((collision) => {
        const shown =
          collision.stays_live === 'loser'
            ? label(collision.loser_status)
            : label(collision.survivor_status)
        const retained =
          collision.stays_live === 'loser'
            ? label(collision.survivor_status)
            : label(collision.loser_status)
        return (
          <Typography
            variant="body2"
            key={`${collision.org_uuid}-${collision.need_id}`}
            sx={{ mb: 0.5 }}
          >
            • {collision.org_name ?? collision.org_uuid}: <strong>{shown}</strong>{' '}
            stays visible, {retained} retained behind it
          </Typography>
        )
      })}
    </Box>
  )
}

/**
 * What approving throws away. Everything else is moved, so this is the part a
 * reviewer cannot get back by merging the other way afterwards.
 */
const MergeDiscards: React.FC<{ item: MergeItem; preview: MergePreview }> = ({
  item,
  preview,
}) => {
  const discards: string[] = []
  const isFunder = item.resource_type === 'funder'

  if (preview.questions_differ) {
    discards.push(
      "The deleted record's application questions. The surviving record's " +
        'are kept, and every moved application is flagged for the ' +
        'organisation to update its answers.',
    )
  }
  discards.push(
    `The deleted record's revision history. The merge itself is recorded as ` +
      'a comment on the surviving record.',
  )
  if (isFunder) {
    discards.push(
      'Any opportunity merged into another loses its own questions and ' +
        'revision history in the same way.',
    )
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Discarded, not moved
      </Typography>
      {discards.map((discard) => (
        <Typography variant="body2" key={discard} sx={{ mb: 0.5 }}>
          • {discard}
        </Typography>
      ))}
    </Box>
  )
}
